import { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import AiTemplateClient, {
    AiChatMessage,
    AiTemplateGroup,
    AiTemplateItem,
    AiTemplatePreview,
} from '../../apiClients/AiTemplateClient';
import ContextualAiWorkspace from './ContextualAiWorkspace';
import { ContextualAiWorkflowAdapter } from './contextualAiTypes';
import { buildContextualRequest } from './contextualAiWire';
import { buildProficiencyGroupAiContext, getProficiencyGroupGuidance } from '../template/proficiencyGroupGuidance';

export type TemplateEditorAiProposalType = 'add' | 'remove' | 'proficiency-weight' | 'group-weight';
export type TemplateEditorAiDecision = 'pending' | 'accepted' | 'rejected';

export interface TemplateEditorAiProposal {
    Id: string;
    Type: TemplateEditorAiProposalType;
    GroupName: string;
    GroupId: string;
    Description: string;
    ProficiencyId?: string;
    CurrentWeight?: number;
    ProposedWeight?: number;
    Item?: AiTemplateItem;
}

interface TemplateEditorAiPanelProps {
    positionId: string;
    positionTitle: string;
    templateName: string;
    profileName: string;
    currentPreview: AiTemplatePreview;
    currentGroupIds: Record<string, string>;
    activeCategoryName?: string;
    disabled?: boolean;
    onAcceptProposal: (proposal: TemplateEditorAiProposal) => Promise<void>;
}

const TemplateEditorAiPanel = ({
    positionId,
    positionTitle,
    templateName,
    profileName,
    currentPreview,
    currentGroupIds,
    activeCategoryName,
    disabled = false,
    onAcceptProposal,
}: TemplateEditorAiPanelProps) => {
    const currentPreviewRef = useRef(currentPreview);
    const currentPreviewSignatureRef = useRef(previewSignature(currentPreview));
    const [messages, setMessages] = useState<AiChatMessage[]>([]);
    const [workingPreview, setWorkingPreview] = useState<AiTemplatePreview | null>(null);
    const [interactionId, setInteractionId] = useState('');
    const [proposals, setProposals] = useState<TemplateEditorAiProposal[]>([]);
    const [decisions, setDecisions] = useState<Record<string, TemplateEditorAiDecision>>({});
    const [applyingId, setApplyingId] = useState('');
    const [error, setError] = useState('');
    const currentPreviewSignature = previewSignature(currentPreview);
    const currentGroupIdsSignature = JSON.stringify(Object.entries(currentGroupIds).sort(([left], [right]) => left.localeCompare(right)));

    useEffect(() => {
        currentPreviewRef.current = currentPreview;
        currentPreviewSignatureRef.current = currentPreviewSignature;

        // The editor remains usable while proposals are open. Rebase the queue
        // whenever its real values change so an old card cannot overwrite a
        // newer manual edit or an already-applied proposal.
        if (workingPreview) {
            const rebasedPreview = rebasePendingTemplateEditorProposals(currentPreview, proposals, decisions);
            const rebased = buildTemplateEditorProposals(currentPreview, rebasedPreview, currentGroupIds);
            setWorkingPreview(rebasedPreview);
            setProposals(rebased);
            setDecisions(current => Object.fromEntries(
                rebased.map(proposal => [proposal.Id, current[proposal.Id] ?? 'pending'])
            ));
        }
        // workingPreview intentionally is not a dependency: setting a new AI
        // response already installs its proposals, while this effect tracks
        // changes to the authoritative editor baseline only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPreviewSignature, currentGroupIdsSignature]);

    const handleAccept = async (proposal: TemplateEditorAiProposal) => {
        setApplyingId(proposal.Id);
        setError('');
        try {
            await onAcceptProposal(proposal);
            setDecisions(current => ({ ...current, [proposal.Id]: 'accepted' }));
            setMessages(current => [
                ...current,
                { role: 'user', content: `I accepted this change: ${describeProposal(proposal)}.` },
            ]);
        } catch (reason) {
            setError('That change could not be applied. Nothing else was changed; please try it again.');
        } finally {
            setApplyingId('');
        }
    };

    const handleReject = async (proposal: TemplateEditorAiProposal) => {
        setWorkingPreview(current => current
            ? revertRejectedProposal(current, currentPreviewRef.current, proposal)
            : current
        );
        setDecisions(current => ({ ...current, [proposal.Id]: 'rejected' }));
        setMessages(current => [
            ...current,
            { role: 'user', content: `I rejected this change: ${describeProposal(proposal)}. Do not propose it again unless I ask.` },
        ]);
    };

    const undoDenial = (proposal: TemplateEditorAiProposal) => {
        setDecisions(current => ({ ...current, [proposal.Id]: 'pending' }));
        setMessages(current => [...current, { role: 'user', content: `Restore this proposed change: ${describeProposal(proposal)}.` }]);
    };

    const applyIncluded = async () => {
        for (const proposal of proposals.filter(item => (decisions[item.Id] ?? 'pending') === 'pending'))
            await handleAccept(proposal);
    };

    const adapter: ContextualAiWorkflowAdapter<TemplateEditorAiProposal[]> = {
        workflow: 'template-editor',
        sessionKey: `template-editor:${positionId}:${templateName}`,
        voiceWorkflow: 'TemplateCreation',
        contextId: positionId || null,
        capabilities: { freeText: true, voice: true, editableContextSummary: false },
        contextMetadata: {
            activeCategoryName: getProficiencyGroupGuidance(activeCategoryName)?.name ?? activeCategoryName ?? null,
            activeCategoryGuidance: buildProficiencyGroupAiContext(activeCategoryName),
        },
        initialMessages: messages.filter(message => message.role !== 'system').map(message => ({ role: message.role as 'user' | 'assistant', content: message.content })),
        progressLabel: `Improving ${profileName || 'selected profile'}`,
        submit: async request => {
            const requestedPreviewSignature = currentPreviewSignatureRef.current;
            const contextualResponse = await new AiTemplateClient().ContinueContextualChat(buildContextualRequest('template-editor', 'generate-now', {
                PositionId: positionId,
                PositionTitle: positionTitle || templateName,
                TemplateName: templateName,
                ProcessType: currentPreview.ProcessType,
                JobDescription: currentPreview.JobDescription || 'Use the existing template and supervisor conversation as the source of truth.',
                Answers: [],
                Messages: [
                    ...(typeof request.metadata?.activeCategoryGuidance === 'string'
                        ? [{ role: 'system' as const, content: request.metadata.activeCategoryGuidance }]
                        : []),
                    ...request.messages.map(({ role, content }) => ({ role, content })),
                ],
                ExistingPreview: workingPreview ?? currentPreview,
                Regenerate: false,
                InteractionId: interactionId || undefined,
                GenerateNow: true,
            }));
            const response = contextualResponse.TemplatePreview;
            if (!response)
                throw new Error('Template improvement did not return a preview.');
            if (requestedPreviewSignature !== currentPreviewSignatureRef.current)
                throw new Error('Template changed while AI was preparing suggestions.');
            const nextProposals = buildTemplateEditorProposals(currentPreviewRef.current, response, currentGroupIds);
            setWorkingPreview(response);
            setInteractionId(response.InteractionId);
            setProposals(nextProposals);
            setDecisions(Object.fromEntries(nextProposals.map(proposal => [proposal.Id, 'pending'])));
            return { assistantMessage: contextualResponse.Rationale || response.AssistantMessage || 'I prepared template changes for review.', contextSummary: contextualResponse.ContextSummary, result: nextProposals };
        },
    };

    return (
        <ContextualAiWorkspace
            adapter={adapter}
            ariaLabel="Template improvement assistant"
            testId="template-editor-ai-panel"
            transcriptTestId="template-editor-ai-scroll"
            composerTestId="template-editor-ai-composer-shell"
            composerContentTestId="template-editor-ai-composer"
            sendAriaLabel="Send"
            composerLabel="Ask AI to improve this template"
            disabled={disabled}
            onMessagesChange={next => setMessages(next)}
            renderResult={() => proposals.length > 0 ? (
                <Box aria-label="AI template proposals">
                    <Alert severity="info" sx={{ mb: 1 }}>Suggestions are included by default. Deny only the changes that should not be applied.</Alert>
                    <Stack spacing={1}>
                        {proposals.map(proposal => {
                            const decision = decisions[proposal.Id] ?? 'pending';
                            const denied = decision === 'rejected';
                            return (
                                <Paper key={proposal.Id} variant="outlined" data-decision={denied ? 'denied' : 'included'} sx={{ p: 1.25, opacity: denied ? 0.55 : 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <ProposalIcon type={proposal.Type} />
                                        <Box sx={{ minWidth: 0, flex: 1 }}><Typography variant="body2" fontWeight={650}>{proposal.Description}</Typography><Typography variant="caption" color="text.secondary">{proposal.GroupName} · {proposalLabel(proposal)}</Typography></Box>
                                        <Chip size="small" label={denied ? 'Denied' : decision === 'accepted' ? 'Applied' : 'Included'} />
                                    </Stack>
                                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                                        {denied
                                            ? <Button size="small" onClick={() => undoDenial(proposal)}>Undo denial</Button>
                                            : decision !== 'accepted' && <Button size="small" color="inherit" onClick={() => void handleReject(proposal)} aria-label={`Deny ${proposal.Description}`}>Deny</Button>}
                                    </Stack>
                                </Paper>
                            );
                        })}
                    </Stack>
                </Box>
            ) : undefined}
            footer={proposals.some(proposal => (decisions[proposal.Id] ?? 'pending') === 'pending') ? (
                <Stack spacing={1} sx={{ flex: 1 }}>
                    {error && <Alert severity="warning">{error}</Alert>}
                    <Button variant="contained" disabled={Boolean(applyingId)} onClick={() => void applyIncluded()}>{applyingId ? <CircularProgress size={16} /> : 'Apply included changes'}</Button>
                </Stack>
            ) : undefined}
        />
    );
};

const ProposalIcon = ({ type }: { type: TemplateEditorAiProposalType }) => {
    if (type === 'add')
        return <AddCircleOutlineRoundedIcon color="success" fontSize="small" />;
    if (type === 'remove')
        return <RemoveCircleOutlineRoundedIcon color="error" fontSize="small" />;
    return <TuneRoundedIcon color="primary" fontSize="small" />;
};

const proposalLabel = (proposal: TemplateEditorAiProposal) => {
    switch (proposal.Type) {
        case 'add': return 'Add proficiency';
        case 'remove': return 'Remove proficiency';
        case 'proficiency-weight': return `Change weight ${proposal.CurrentWeight} → ${proposal.ProposedWeight}`;
        case 'group-weight': return `Change group weight ${proposal.CurrentWeight} → ${proposal.ProposedWeight}`;
    }
};

const describeProposal = (proposal: TemplateEditorAiProposal) =>
    `${proposalLabel(proposal)} "${proposal.Description}" in ${proposal.GroupName}`;

export const buildTemplateEditorProposals = (
    current: AiTemplatePreview,
    suggested: AiTemplatePreview,
    currentGroupIds: Record<string, string>,
): TemplateEditorAiProposal[] => {
    const proposals: TemplateEditorAiProposal[] = [];
    const currentGroups = new Map(current.ProficiencyGroups.map(group => [group.ProficiencyGroup, group]));

    suggested.ProficiencyGroups.forEach(suggestedGroup => {
        const currentGroup = currentGroups.get(suggestedGroup.ProficiencyGroup);
        const groupId = currentGroupIds[suggestedGroup.ProficiencyGroup] ?? '';
        if (!currentGroup)
            return;

        if (currentGroup.Weight !== suggestedGroup.Weight) {
            proposals.push({
                Id: `group-weight|${suggestedGroup.ProficiencyGroup}`,
                Type: 'group-weight',
                GroupName: suggestedGroup.ProficiencyGroup,
                GroupId: groupId,
                Description: `${suggestedGroup.ProficiencyGroup} group`,
                CurrentWeight: currentGroup.Weight,
                ProposedWeight: suggestedGroup.Weight,
            });
        }

        proposals.push(...buildGroupProposals(currentGroup, suggestedGroup, groupId));
        currentGroups.delete(suggestedGroup.ProficiencyGroup);
    });

    currentGroups.forEach(group => {
        const groupId = currentGroupIds[group.ProficiencyGroup] ?? '';
        group.Proficiencies.forEach(item => proposals.push(removeProposal(group.ProficiencyGroup, groupId, item)));
    });

    return proposals;
};

const buildGroupProposals = (
    currentGroup: AiTemplateGroup,
    suggestedGroup: AiTemplateGroup,
    groupId: string,
) => {
    const proposals: TemplateEditorAiProposal[] = [];
    const unmatchedCurrent = [...currentGroup.Proficiencies];

    suggestedGroup.Proficiencies.forEach(suggestedItem => {
        const currentIndex = unmatchedCurrent.findIndex(currentItem => itemsMatch(currentItem, suggestedItem));
        if (currentIndex < 0) {
            proposals.push({
                Id: `add|${suggestedGroup.ProficiencyGroup}|${itemKey(suggestedItem)}`,
                Type: 'add',
                GroupName: suggestedGroup.ProficiencyGroup,
                GroupId: groupId,
                Description: suggestedItem.Description,
                ProposedWeight: suggestedItem.Weight,
                Item: suggestedItem,
            });
            return;
        }

        const currentItem = unmatchedCurrent.splice(currentIndex, 1)[0];
        if (currentItem.Weight !== suggestedItem.Weight) {
            proposals.push({
                Id: `proficiency-weight|${currentItem.TemplateProficiencyId ?? itemKey(currentItem)}`,
                Type: 'proficiency-weight',
                GroupName: suggestedGroup.ProficiencyGroup,
                GroupId: groupId,
                Description: currentItem.Description,
                ProficiencyId: currentItem.TemplateProficiencyId ?? undefined,
                CurrentWeight: currentItem.Weight,
                ProposedWeight: suggestedItem.Weight,
                Item: suggestedItem,
            });
        }
    });

    unmatchedCurrent.forEach(item => proposals.push(removeProposal(currentGroup.ProficiencyGroup, groupId, item)));
    return proposals;
};

const removeProposal = (groupName: string, groupId: string, item: AiTemplateItem): TemplateEditorAiProposal => ({
    Id: `remove|${item.TemplateProficiencyId ?? `${groupName}|${itemKey(item)}`}`,
    Type: 'remove',
    GroupName: groupName,
    GroupId: groupId,
    Description: item.Description,
    ProficiencyId: item.TemplateProficiencyId ?? undefined,
    CurrentWeight: item.Weight,
    Item: item,
});

const itemsMatch = (current: AiTemplateItem, suggested: AiTemplateItem) => {
    if (current.TemplateProficiencyId && suggested.TemplateProficiencyId === current.TemplateProficiencyId)
        return true;
    return itemKey(current) === itemKey(suggested);
};

const itemKey = (item: AiTemplateItem) => [
    item.Type,
    item.IsNegative ? 'negative' : 'positive',
    item.Description.trim().replace(/\s+/g, ' ').toLowerCase(),
].join('|');

const previewSignature = (preview: AiTemplatePreview) => JSON.stringify(
    preview.ProficiencyGroups.map(group => ({
        name: group.ProficiencyGroup,
        weight: group.Weight,
        items: group.Proficiencies.map(item => ({
            id: item.TemplateProficiencyId,
            key: itemKey(item),
            weight: item.Weight,
        })),
    }))
);

export const revertRejectedProposal = (
    working: AiTemplatePreview,
    current: AiTemplatePreview,
    proposal: TemplateEditorAiProposal,
): AiTemplatePreview => {
    const groups = working.ProficiencyGroups.map(group => ({
        ...group,
        Proficiencies: [...group.Proficiencies],
    }));
    const workingGroup = groups.find(group => group.ProficiencyGroup === proposal.GroupName);
    const currentGroup = current.ProficiencyGroups.find(group => group.ProficiencyGroup === proposal.GroupName);

    if (!workingGroup || !currentGroup)
        return { ...working, ProficiencyGroups: groups };

    if (proposal.Type === 'group-weight') {
        workingGroup.Weight = currentGroup.Weight;
    } else if (proposal.Type === 'add' && proposal.Item) {
        workingGroup.Proficiencies = workingGroup.Proficiencies.filter(item => !itemsMatch(item, proposal.Item));
    } else if (proposal.Type === 'remove' && proposal.Item) {
        const exists = workingGroup.Proficiencies.some(item => itemsMatch(item, proposal.Item));
        if (!exists)
            workingGroup.Proficiencies.push({ ...proposal.Item });
    } else if (proposal.Type === 'proficiency-weight') {
        const currentItem = currentGroup.Proficiencies.find(item =>
            item.TemplateProficiencyId === proposal.ProficiencyId
            || (proposal.Item ? itemsMatch(item, proposal.Item) : false)
        );
        const workingItem = workingGroup.Proficiencies.find(item =>
            item.TemplateProficiencyId === proposal.ProficiencyId
            || (proposal.Item ? itemsMatch(item, proposal.Item) : false)
        );
        if (currentItem && workingItem)
            workingItem.Weight = currentItem.Weight;
    }

    return { ...working, ProficiencyGroups: groups };
};

export const rebasePendingTemplateEditorProposals = (
    current: AiTemplatePreview,
    proposals: TemplateEditorAiProposal[],
    decisions: Record<string, TemplateEditorAiDecision>,
): AiTemplatePreview => {
    const groups = current.ProficiencyGroups.map(group => ({
        ...group,
        Proficiencies: group.Proficiencies.map(item => ({ ...item })),
    }));

    proposals
        .filter(proposal => (decisions[proposal.Id] ?? 'pending') === 'pending')
        .forEach(proposal => {
            const group = groups.find(candidate => candidate.ProficiencyGroup === proposal.GroupName);
            if (!group)
                return;

            if (proposal.Type === 'group-weight') {
                if (group.Weight === proposal.CurrentWeight && proposal.ProposedWeight)
                    group.Weight = proposal.ProposedWeight;
                return;
            }

            if (proposal.Type === 'add' && proposal.Item) {
                if (!group.Proficiencies.some(item => itemsMatch(item, proposal.Item)))
                    group.Proficiencies.push({ ...proposal.Item });
                return;
            }

            const itemIndex = group.Proficiencies.findIndex(item =>
                item.TemplateProficiencyId === proposal.ProficiencyId
                || (proposal.Item ? itemsMatch(item, proposal.Item) : false)
            );
            if (itemIndex < 0)
                return;

            if (proposal.Type === 'remove') {
                if (proposal.Item && itemStateMatches(group.Proficiencies[itemIndex], proposal.Item))
                    group.Proficiencies.splice(itemIndex, 1);
            } else if (
                proposal.Type === 'proficiency-weight'
                && group.Proficiencies[itemIndex].Weight === proposal.CurrentWeight
                && proposal.ProposedWeight
            ) {
                group.Proficiencies[itemIndex].Weight = proposal.ProposedWeight;
            }
        });

    return { ...current, ProficiencyGroups: groups };
};

const itemStateMatches = (current: AiTemplateItem, expected: AiTemplateItem) =>
    itemKey(current) === itemKey(expected) && current.Weight === expected.Weight;

export default TemplateEditorAiPanel;
